import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Breadcrumb,
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  Select,
  Skeleton,
  Spin,
  Tag,
} from "antd";
import { RightOutlined, SearchOutlined, TeamOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

import PageTitle from "../../components/PageTitle";
import DashboardButton from "../../components/DashboardButton";
import TenantLink from "../../components/TenantLink";
import useTenantNavigate from "../../hooks/useTenantNavigate";
import { fetchData, postData } from "../../api/apiService";
import { CLIENTS, DOCUMENTS } from "../../api/endpointMap";
import DeleteAllNoticesButton from "../../components/DeleteAllNoticesButton";

const { RangePicker } = DatePicker;
const { Option } = Select;

// E-Proceedings list endpoint — same one the notices view uses. Called here
// WITHOUT a `client` param so it returns matching notices across ALL clients
// (still scoped to the org + the user's assignments). The backend's
// notice_communication_reference_id filter ORs against notice_din, so a single
// search term matches a notice by its ID/DIN OR its reference/communication id.
const EP_ENDPOINT = "/api/v1/income-tax/e-proceedings";

/**
 * Income Tax > Total Notices client-selection step.
 *
 * The "Total Notices" card on the Income Tax dashboard lands here first: pick an
 * active client, then open that client's grouped Total Notices (Eproceedings
 * route `eproceedings/:clientId?type=total_notices`).
 *
 * Two ways to find a client:
 *  - the top box searches the client list by name / PAN (local, instant);
 *  - the "Find by notice" bar searches notice-level detail (notice id /
 *    reference / communication id, assessment year, response status, issued
 *    date) across all clients and narrows the list to ONLY the clients that
 *    own a matching notice, with a per-client match count.
 */
export default function ITTotalNoticesClients() {
  const navigate = useTenantNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Bumped after a bulk delete so the client list (and its notice counts)
  // refetches instead of showing stale numbers.
  const [reloadKey, setReloadKey] = useState(0);

  // "Find by notice" filters. When any is set, we query notices across clients
  // and keep only the clients that have a match.
  const [noticeQ, setNoticeQ] = useState("");
  const [assessmentYear, setAssessmentYear] = useState("");
  const [responseStatus, setResponseStatus] = useState(undefined);
  const [issuedDate, setIssuedDate] = useState(null); // [dayjs, dayjs] | null

  // Result of the notice lookup: null = no notice filter active (show all
  // clients); otherwise { ids:Set<clientId>, counts:Map<clientId,number> }.
  const [matchInfo, setMatchInfo] = useState(null);
  const [noticeLoading, setNoticeLoading] = useState(false);

  const hasNoticeFilter =
    !!noticeQ.trim() ||
    !!assessmentYear.trim() ||
    !!responseStatus ||
    (Array.isArray(issuedDate) &&
      issuedDate.length === 2 &&
      issuedDate[0] &&
      issuedDate[1]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        // Paginated LIST endpoint (same one the Clients page uses);
        // only_disabled:false = active clients. page_size is generous so the
        // picker isn't paginated.
        const data = await fetchData(CLIENTS.INCOME_TAX_LIST, {
          page: 1,
          page_size: 1000,
          only_disabled: false,
        });
        if (alive) setClients(data?.items || []);
      } catch (err) {
        if (alive) {
          toast.error(typeof err === "string" ? err : "Failed to load clients");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  // Debounced notice lookup: whenever the notice filters change, fetch matching
  // notices across all clients and build the id-set + per-client counts.
  useEffect(() => {
    if (!hasNoticeFilter) {
      setMatchInfo(null);
      setNoticeLoading(false);
      return;
    }
    let alive = true;
    setNoticeLoading(true);
    const handle = setTimeout(async () => {
      try {
        const params = {
          start: 0,
          page_length: 1000,
          notice_mode: "total",
        };
        if (noticeQ.trim()) {
          // Matches notice_communication_reference_id OR notice_din on the BE.
          params.notice_communication_reference_id = noticeQ.trim();
        }
        if (assessmentYear.trim()) params.assessment_year = assessmentYear.trim();
        if (responseStatus) params.response_status = responseStatus;
        if (
          Array.isArray(issuedDate) &&
          issuedDate.length === 2 &&
          issuedDate[0] &&
          issuedDate[1]
        ) {
          params.sent_from = issuedDate[0].format("YYYY-MM-DD");
          params.sent_to = issuedDate[1].format("YYYY-MM-DD");
        }

        const resp = await fetchData(EP_ENDPOINT, params);
        const records = resp?.data?.records || [];
        const counts = new Map();
        records.forEach((r) => {
          const cid = r.client; // BE serializes client_id as `client`
          if (!cid) return;
          counts.set(cid, (counts.get(cid) || 0) + 1);
        });
        if (alive) {
          setMatchInfo({ ids: new Set(counts.keys()), counts });
        }
      } catch (err) {
        if (alive) {
          toast.error(
            typeof err === "string" ? err : "Failed to search notices",
          );
          setMatchInfo({ ids: new Set(), counts: new Map() });
        }
      } finally {
        if (alive) setNoticeLoading(false);
      }
    }, 400);
    return () => {
      alive = false;
      clearTimeout(handle);
    };
  }, [noticeQ, assessmentYear, responseStatus, issuedDate, hasNoticeFilter]);

  const filtered = useMemo(() => {
    // Step 1 — narrow to clients that own a matching notice (if a notice filter
    // is active). matchInfo === null means no notice filter → keep all.
    let base = clients;
    if (matchInfo) {
      base = clients.filter((c) => matchInfo.ids.has(c.name || c.id));
    }
    // Step 2 — apply the name/PAN text search on top.
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter((c) => {
      const name = (c.custom_client_name || c.client_name || "").toLowerCase();
      // The IT clients endpoint returns the PAN as `username` (the portal login
      // IS the PAN); there is no `pan` field, so matching on `c.pan` alone
      // never hit. Keep `pan` as a fallback in case a caller supplies it.
      const pan = (c.username || c.pan || "").toLowerCase();
      // Portal-registered assessee name is worth matching too - users often
      // search the name the portal shows rather than the local client name.
      const assessee = (c.portal_name_of_assessee || "").toLowerCase();
      return name.includes(q) || pan.includes(q) || assessee.includes(q);
    });
  }, [clients, search, matchInfo]);

  // --- "Delete All Notices" for the currently listed clients -----------------
  // Deletes by CLIENT id rather than notice id: one call to
  // /income-tax/delete-client-documents removes every notice of those clients
  // (both IT doctypes + child rows + Case Details chain + S3), so there is no
  // need to enumerate/paginate notice ids here. Scope follows what is on
  // screen — the name/PAN search and the "find by notice detail" filters both
  // narrow `filtered`, so filtering first limits the blast radius.
  const fetchClientIdsForDelete = useCallback(
    async () => filtered.map((c) => c.name || c.id).filter(Boolean),
    [filtered],
  );

  const deleteNoticesForClients = useCallback(async (clientIds) => {
    if (!clientIds || clientIds.length === 0) return;
    await postData(DOCUMENTS.DELETE_CLIENT_NOTICES_INCOME_TAX, {
      client_names: JSON.stringify(clientIds),
    });
  }, []);

  const clearNoticeFilters = useCallback(() => {
    setNoticeQ("");
    setAssessmentYear("");
    setResponseStatus(undefined);
    setIssuedDate(null);
  }, []);

  const openClientNotices = useCallback(
    (c) => {
      const clientId = c.name || c.id;
      if (!clientId) return;
      navigate(`/income-tax/eproceedings/${clientId}?type=total_notices`);
    },
    [navigate],
  );

  return (
    <>
      <div className="flex w-full justify-between items-center">
        <Breadcrumb
          items={[
            {
              title: (
                <TenantLink
                  className="exempt-from-readonly"
                  to={{ pathname: "/" }}
                >
                  Home
                </TenantLink>
              ),
            },
            {
              title: (
                <TenantLink
                  className="exempt-from-readonly"
                  to={{ pathname: "/income-tax" }}
                >
                  Income Tax
                </TenantLink>
              ),
            },
            { title: "Total Notices" },
          ]}
          style={{ marginBottom: "10px", color: "#151c40" }}
        />
        <DashboardButton serviceType="income_tax" />
      </div>

      <PageTitle title="Total Notices" />

      <div className="mt-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="text-slate-500">
            Select a client to view their notices.
          </div>
          {/* Deletes every Income-Tax notice of the clients CURRENTLY LISTED.
              Placed here (not per-client) because this page has no notice
              selection of its own; narrowing the search / notice filters first
              narrows what this removes. */}
          <DeleteAllNoticesButton
            fetchAllIds={fetchClientIdsForDelete}
            deleteByIds={deleteNoticesForClients}
            onDone={() => setReloadKey((k) => k + 1)}
            disabled={filtered.length === 0}
            noun="clients"
            className=""
            describe={(n) =>
              `This will permanently delete EVERY Income Tax notice belonging to ${n} client${
                n > 1 ? "s" : ""
              }${
                hasNoticeFilter || search.trim()
                  ? " (the clients currently listed)"
                  : " - every client in this organisation"
              }. The clients themselves are kept.`
            }
            okLabel={(n) =>
              `Delete notices of ${n} client${n > 1 ? "s" : ""}`
            }
          />
        </div>

        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search client by name or PAN..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360, marginBottom: 16 }}
        />

        {/* Find by notice — searches notice-level detail across all clients and
            narrows the list to clients that own a matching notice. */}
        <div className="mb-4 p-3 rounded-xl bg-gray-50 border border-gray-200">
          <div className="text-xs font-medium text-gray-500 mb-2">
            Find a client by notice detail
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-500 mb-1">
                Notice ID / Reference / Communication ID
              </label>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                style={{ width: 280 }}
                placeholder="Search by notice id / reference id"
                value={noticeQ}
                onChange={(e) => setNoticeQ(e.target.value)}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-500 mb-1">
                Assessment Year
              </label>
              <Input
                allowClear
                style={{ width: 150 }}
                placeholder="e.g. 2024-25"
                value={assessmentYear}
                onChange={(e) => setAssessmentYear(e.target.value)}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-500 mb-1">
                Response Status
              </label>
              <Select
                allowClear
                style={{ width: 190 }}
                placeholder="All statuses"
                value={responseStatus}
                onChange={(v) => setResponseStatus(v || undefined)}
              >
                <Option value="Pending">Pending</Option>
                <Option value="Response Generated">Response Generated</Option>
                <Option value="Response Finalized">Response Finalized</Option>
                <Option value="Submitted">Submitted</Option>
              </Select>
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-500 mb-1">
                Issued Date
              </label>
              <RangePicker
                format="YYYY-MM-DD"
                style={{ width: 250 }}
                placeholder={["Start Date", "End Date"]}
                value={issuedDate}
                onChange={(dates) =>
                  setIssuedDate(
                    dates && dates.length === 2 ? [dates[0], dates[1]] : null,
                  )
                }
              />
            </div>
            {hasNoticeFilter && (
              <Button onClick={clearNoticeFilters}>Clear filters</Button>
            )}
          </div>
          {hasNoticeFilter && (
            <div className="text-xs text-slate-500 mt-2">
              {noticeLoading ? (
                <span>
                  <Spin size="small" className="mr-2" />
                  Searching notices...
                </span>
              ) : (
                <span>
                  Showing {filtered.length} client
                  {filtered.length === 1 ? "" : "s"} with matching notices.
                </span>
              )}
            </div>
          )}
        </div>

        {/* All-clients combined view. The full Total Notices table for every
            client (Download PDF/Excel, Delete Notice, column filters) already
            lives at `notices?type=total_notices` — this picker replaced it as
            the card's landing page, which made module-level report download
            unreachable. This entry restores it without duplicating any of
            that machinery. */}
        <Card
          hoverable
          onClick={() => navigate("/income-tax/notices?type=total_notices")}
          className="cursor-pointer mb-4"
          style={{ borderColor: "#1e40af", background: "#f5f8ff" }}
          styles={{ body: { padding: "14px 18px" } }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <TeamOutlined style={{ fontSize: 20, color: "#1e40af" }} />
              <div className="min-w-0">
                <div className="font-semibold text-slate-800">
                  All Clients - combined view
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Download reports (PDF / Excel) or manage notices across every
                  client
                </div>
              </div>
            </div>
            <RightOutlined className="text-slate-400 shrink-0" />
          </div>
        </Card>

        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : filtered.length === 0 ? (
          <Empty
            description={
              hasNoticeFilter
                ? "No clients have notices matching your search"
                : search
                ? "No clients match your search"
                : "No active clients"
            }
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((c) => {
              const clientId = c.name || c.id;
              const displayName =
                c.custom_client_name || c.client_name || "Unnamed client";
              const matchCount = matchInfo
                ? matchInfo.counts.get(clientId) || 0
                : null;
              return (
                <Card
                  key={clientId}
                  hoverable
                  onClick={() => openClientNotices(c)}
                  className="cursor-pointer"
                  styles={{ body: { padding: "16px 18px" } }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 truncate">
                        {displayName}
                      </div>
                      {/* PAN comes back as `username` (the IT portal login is
                          the PAN); reading `c.pan` meant this line never
                          rendered. */}
                      {c.username || c.pan ? (
                        <div className="text-xs text-slate-400 mt-1">
                          PAN: {c.username || c.pan}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {matchCount !== null ? (
                        <Tag color="orange">
                          {matchCount} match{matchCount === 1 ? "" : "es"}
                        </Tag>
                      ) : typeof c.total_notices === "number" ? (
                        <Tag color="geekblue">{c.total_notices}</Tag>
                      ) : null}
                      <RightOutlined className="text-slate-400" />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
